export class TennisScoreEngine {
  /**
   * @param {Object} options
   *  - matchFormats: tableau de codes de format par set ["STD","STD","NON","NON","NON"]
   *  - formatMatch: "BEST_OF_3" (2 sets gagnants) ou "BEST_OF_5" (3 sets gagnants)
   */
  constructor(options = {}) {
     const {
      matchFormats = ["STD", "STD", "STD", "NON", "NON"],
      formatMatch = "BEST_OF_3",
      premierServeur = "J1",
    } = options;

    this.matchFormats = matchFormats;
    this.formatMatch = formatMatch; // 2 ou 3 sets gagnants
    this.premierServeur = premierServeur;
    this.lastTieBreakScores = [];
    this.reset();
  }

  reset() {
    this.points = [];
    this.currentSet = 0;
    this.sets = [[0, 0]]; // [ [J1, J2], ... ]
    this.jeuPoints = [0, 0];
    this.server = this.premierServeur;
    this.tieBreakActive = false;
    this.superTieBreakActive = false;
    this.tieBreakPoints = [0, 0];
    this.matchFinished = false;
    this.tieBreakFirstServer = null;  // 🆕 Premier serveur du tie-break
  }

   saveState() {
        return {
            points: this.points,
            currentSet: this.currentSet,
            sets: this.sets,
            jeuPoints: this.jeuPoints,
            server: this.server,
            tieBreakActive: this.tieBreakActive,
            superTieBreakActive: this.superTieBreakActive,
            tieBreakPoints: this.tieBreakPoints,
            matchFinished: this.matchFinished,
            lastTieBreakScores: this.lastTieBreakScores,
            tieBreakFirstServer: this.tieBreakFirstServer,
            startPointIndex: this.startPointIndex || 0  // 🆕 Pour les stats
        };
    }

    restoreState(state) {
        Object.assign(this, state);
    }

  /**
   * Initialise le moteur à partir d'un score en cours (match commencé sans saisie)
   * @param {Object} config
   *   - sets: tableau des sets terminés ex: [[6,3],[4,6]]
   *   - jeuScore: jeux dans le set en cours ex: [4,3]
   *   - pointsJeu: points dans le jeu ex: [3,1] (0=0, 1=15, 2=30, 3=40, 4=AD)
   *   - server: serveur actuel "J1" ou "J2"
   *   - tieBreakActive: boolean
   *   - tieBreakPoints: [0,0]
   */
  initializeFromScore(config = {}) {
    const {
      sets = [],
      jeuScore = [0, 0],
      pointsJeu = [0, 0],
      server = "J1",
      tieBreakActive = false,
      tieBreakPoints = [0, 0],
      superTieBreakActive = false,
    } = config;

    // Réinitialiser
    this.reset();

    // Reconstruire les sets terminés
    if (sets.length > 0) {
      this.sets = [...sets.map(s => [...s]), [0, 0]];
      this.currentSet = sets.length;
      // Initialiser lastTieBreakScores avec null pour chaque set passé
      this.lastTieBreakScores = sets.map(() => null);
    }

    // Score du jeu en cours
    this.sets[this.currentSet] = [jeuScore[0] || 0, jeuScore[1] || 0];

    // Points dans le jeu en cours
    this.jeuPoints = [pointsJeu[0] || 0, pointsJeu[1] || 0];

    // Tie-break
    this.tieBreakActive = tieBreakActive;
    this.superTieBreakActive = superTieBreakActive;
    this.tieBreakPoints = [...tieBreakPoints];

    // Serveur
    this.server = server;
    this.premierServeur = server;

    // Marquer le point de départ pour les stats
    this.startPointIndex = 0; // Les points ajoutés après seront indexés à partir de 0

    console.log('⚡ Score initialisé:', {
      sets: this.sets,
      currentSet: this.currentSet,
      jeuPoints: this.jeuPoints,
      server: this.server
    });
  }


  getSetsToWin() {
    return this.formatMatch === "BEST_OF_5" ? 3 : 2;
  }

  getSetsWon() {
    let j1 = 0;
    let j2 = 0;
    for (const [s1, s2] of this.sets) {
      if (s1 > s2) j1++;
      else if (s2 > s1) j2++;
    }
    return { j1, j2 };
  }

  isMatchFinished() {
    const { j1, j2 } = this.getSetsWon();
    return j1 >= this.getSetsToWin() || j2 >= this.getSetsToWin();
  }


  addPoint(winner, pointData, codePoint = null) {
      if (this.matchFinished) {
          console.log("🏁 Match terminé, plus de points ajoutés.");
          return;
      }

      console.log(`POINT ${winner}`);

      // Capturer le score AVANT le point
      const scoreAvant = this.getCurrentScore();

      // Ajouter le point avec toutes les métadonnées
      this.points.push({ 
        pointNumero: this.points.length + 1,  // 🆕 AJOUT numéro du point
        winner, 
        pointData,
        codePoint,  // 🆕 STOCKER le code du point
        server: this.server,
        setNumero: this.currentSet + 1,
        jeuNumero: this.getJeuNumero(),
        scoreAvantPoint: scoreAvant.jeuScore,
        scoreApresPoint: null,
        formatSet: this.matchFormats[this.currentSet] || "STD", 
        formatMatch: this.formatMatch
      });

      const format = this.matchFormats[this.currentSet] || "STD";    
      console.log("🔍 DEBUG format:", format, "currentSet:", this.currentSet);

      // ✅ FONCTION HELPER pour mettre à jour le score après
      const updateScoreApres = () => {
          const scoreApres = this.getCurrentScore();
          this.points[this.points.length - 1].scoreApresPoint = scoreApres.jeuScore;
      };

      if (format === "NON" && this.currentSet > 0) {
          console.log("⛔ Format NON sur ce set, aucun point joué.");
          updateScoreApres();  // ✅ Mise à jour avant return
          return;
      }

      // SUPER TIE-BREAK S10
      if (format === "S10" || this.superTieBreakActive) {
          // 🆕 CRITIQUE : Initialiser tieBreakFirstServer au tout premier point
          if (this.tieBreakPoints[0] === 0 && this.tieBreakPoints[1] === 0) {
              this.tieBreakFirstServer = this.server;
              console.log(`🎾 Premier serveur du S10 (début): ${this.tieBreakFirstServer}`);
          }
          
          // ✅ INCRÉMENTER LES POINTS D'ABORD
          if (winner === "J1") this.tieBreakPoints[0]++;
          else this.tieBreakPoints[1]++;

          console.log(`🚀 S10: ${this.tieBreakPoints[0]}-${this.tieBreakPoints[1]}`);
          
          // ✅ CALCULER LE SERVEUR DU **PROCHAIN** POINT (donc +1)
          const currentPointNumber = this.tieBreakPoints[0] + this.tieBreakPoints[1];
          const nextPointNumber = currentPointNumber + 1; // ← AJOUT DU +1 !
          
          let nextServer;
          if (nextPointNumber === 2) {
              // Le point 2 sera servi par other
              nextServer = (this.tieBreakFirstServer === 'J1' ? 'J2' : 'J1');
          } else if (nextPointNumber === 1) {
              // Le point 1 sera servi par firstServer (ne devrait jamais arriver ici)
              nextServer = this.tieBreakFirstServer;
          } else {
              // Points 2-3: other (groupe 0)
              // Points 4-5: first (groupe 1)
              // Points 6-7: other (groupe 2)
              const groupe = Math.floor((nextPointNumber - 2) / 2);
              const isOtherServer = (groupe % 2 === 0);
              
              nextServer = isOtherServer
                  ? (this.tieBreakFirstServer === 'J1' ? 'J2' : 'J1')
                  : this.tieBreakFirstServer;
          }
          
          console.log(`🔍 Après point ${currentPointNumber}: Prochain point ${nextPointNumber} → Serveur = ${nextServer}`);
          
          this.server = nextServer;

          const tbTarget = 10;
          if (
              Math.max(...this.tieBreakPoints) >= tbTarget &&
              Math.abs(this.tieBreakPoints[0] - this.tieBreakPoints[1]) >= 2
          ) {
              console.log(`✅ FIN S10 ${this.tieBreakPoints[0]}-${this.tieBreakPoints[1]}`);
              this.finishTieBreak();
          }
          updateScoreApres();
          return;
      }
      
      // TIE-BREAK normal (STD / T10)
      if (this.tieBreakActive) {
          // 🆕 CRITIQUE : Initialiser tieBreakFirstServer au tout premier point
          if (this.tieBreakPoints[0] === 0 && this.tieBreakPoints[1] === 0) {
              this.tieBreakFirstServer = this.server;
              console.log(`🎾 Premier serveur du TB (début): ${this.tieBreakFirstServer}`);
          }
          
          // ✅ INCRÉMENTER LES POINTS D'ABORD
          if (winner === "J1") this.tieBreakPoints[0]++;
          else this.tieBreakPoints[1]++;

          console.log(`TB ${format}: ${this.tieBreakPoints[0]}-${this.tieBreakPoints[1]}`);
          
          // ✅ CALCULER LE SERVEUR DU **PROCHAIN** POINT (donc +1)
          const currentPointNumber = this.tieBreakPoints[0] + this.tieBreakPoints[1];
          const nextPointNumber = currentPointNumber + 1; // ← AJOUT DU +1 !
          
          let nextServer;
          if (nextPointNumber === 2) {
              // Le point 2 sera servi par other
              nextServer = (this.tieBreakFirstServer === 'J1' ? 'J2' : 'J1');
          } else if (nextPointNumber === 1) {
              // Le point 1 sera servi par firstServer (ne devrait jamais arriver ici)
              nextServer = this.tieBreakFirstServer;
          } else {
              // Points 2-3: other (groupe 0)
              // Points 4-5: first (groupe 1)
              // Points 6-7: other (groupe 2)
              const groupe = Math.floor((nextPointNumber - 2) / 2);
              const isOtherServer = (groupe % 2 === 0);
              
              nextServer = isOtherServer
                  ? (this.tieBreakFirstServer === 'J1' ? 'J2' : 'J1')
                  : this.tieBreakFirstServer;
          }
          
          console.log(`🔍 Après point ${currentPointNumber}: Prochain point ${nextPointNumber} → Serveur = ${nextServer}`);
          
          this.server = nextServer;

          const tbTarget = format === "T10" ? 10 : 7;
          if (
              Math.max(...this.tieBreakPoints) >= tbTarget &&
              Math.abs(this.tieBreakPoints[0] - this.tieBreakPoints[1]) >= 2
          ) {
              console.log(`✅ FIN TB ${format}`);
              this.finishTieBreak();
          }
          updateScoreApres();
          return;
      }

      // JEU NORMAL
      if (winner === "J1") this.jeuPoints[0]++;
      else this.jeuPoints[1]++;

      console.log(`Jeu: ${this.jeuPoints[0]}-${this.jeuPoints[1]}`);

      const isNoAd = format === "NOAD" || format === "NOADT10";

      if (isNoAd) {
          if (this.jeuPoints[0] === 4 && this.jeuPoints[1] === 3) {
              this.finishNormalJeu();
              updateScoreApres();  // ✅ Mise à jour
              return;
          }
          if (this.jeuPoints[0] === 3 && this.jeuPoints[1] === 4) {
              this.finishNormalJeu();
              updateScoreApres();  // ✅ Mise à jour
              return;
          }
      }

      // Logique CLASSIQUE (avantage)
      if (
          (this.jeuPoints[0] >= 4 && this.jeuPoints[0] - this.jeuPoints[1] >= 2) ||
          (this.jeuPoints[1] >= 4 && this.jeuPoints[1] - this.jeuPoints[0] >= 2)
      ) {
          this.finishNormalJeu();
      }

      updateScoreApres();  // ✅ Mise à jour finale
  }

  finishNormalJeu() {
    const format = this.matchFormats[this.currentSet] || "NON";

    // SUPER TIE-BREAK S10 dès début du set
    if (format === "S10") {
      console.log("🚀 SUPER TIE-BREAK S10 DÈS DÉBUT !");
      this.superTieBreakActive = true;
      this.tieBreakPoints = [0, 0];
      this.tieBreakFirstServer = null;  // 🆕 RESET
      this.jeuPoints = [0, 0];
      return;
    }

    // ✅ Incrémenter le score du set
    if (this.jeuPoints[0] > this.jeuPoints[1]) {
      this.sets[this.currentSet][0]++;
    } else {
      this.sets[this.currentSet][1]++;
    }

    const [gamesJ1, gamesJ2] = this.sets[this.currentSet];

    console.log(
      `SCORE SET ${this.currentSet + 1}: ${gamesJ1}-${gamesJ2}`
    );

    // 6-6 → TIE-BREAK (STD / T10)
    if (gamesJ1 === 6 && gamesJ2 === 6) {
      console.log("🚀 TIE-BREAK START (6-6)");
      this.tieBreakActive = true;
      this.tieBreakPoints = [0, 0];
      this.tieBreakFirstServer = null;  // 🆕 RESET
      this.jeuPoints = [0, 0];
      // ✅ CRITIQUE : Changer le serveur avant d'entrer en TB
      // Le premier serveur du TB est celui qui devait servir le jeu suivant
      this.server = this.server === "J1" ? "J2" : "J1";
      console.log(`🎾 Premier serveur du TB: ${this.server}`);
      return;
    }

    // FIN SET CLASSIQUE (6-4, 7-5, etc.)
    if (
      (gamesJ1 >= 6 && gamesJ1 - gamesJ2 >= 2) ||
      (gamesJ2 >= 6 && gamesJ2 - gamesJ1 >= 2)
    ) {
      console.log(`🎯 SET ${this.currentSet + 1} TERMINÉ !`);

      // Vérifier si le match est terminé (sets gagnés)
      if (this.isMatchFinished()) {
        this.matchFinished = true;
        console.log("🏁 MATCH TERMINÉ (sets gagnants atteints).");
        this.jeuPoints = [0, 0];
        return;
      }

      // Passer au set suivant si on a encore un format non "NON"
      this.currentSet++;
      const nextFormat = this.matchFormats[this.currentSet] || "NON";
      if (nextFormat === "NON") {
        // Plus de set configuré → match terminé
        this.matchFinished = true;
        console.log("🏁 MATCH TERMINÉ (plus de set configuré).");
      } else {
        this.sets.push([0, 0]);
        console.log(`🎯 SET ${this.currentSet + 1} COMMENCÉ`);
      }
    }

    // RESET JEU + changement de serveur (si match non terminé)
    this.jeuPoints = [0, 0];
    if (!this.matchFinished) {
      this.server = this.server === "J1" ? "J2" : "J1";
    }
  }

  finishTieBreak() {
    console.log(`FIN TB: ${this.tieBreakPoints[0]}-${this.tieBreakPoints[1]}`);

    const format = this.matchFormats[this.currentSet] || "NON";
    const tbJ1Score = this.tieBreakPoints[0];
    const tbJ2Score = this.tieBreakPoints[1];
    
    if (!this.lastTieBreakScores) this.lastTieBreakScores = [];

    if (format === "S10") {
      if (this.tieBreakPoints[0] > this.tieBreakPoints[1]) {
        this.sets[this.currentSet] = [1, 0];
        console.log(`✅ S10: 1-0 (${tbJ1Score}-${tbJ2Score})`);
      } else {
        this.sets[this.currentSet] = [0, 1];
        console.log(`✅ S10: 0-1 (${tbJ1Score}-${tbJ2Score})`);
      }
      this.lastTieBreakScores[this.currentSet] = [tbJ1Score, tbJ2Score];
    } else {
      this.sets[this.currentSet] =
        this.tieBreakPoints[0] > this.tieBreakPoints[1] ? [7, 6] : [6, 7];
      this.lastTieBreakScores[this.currentSet] = [tbJ1Score, tbJ2Score];
      console.log(`✅ ${format}: 7-6 (${tbJ1Score}-${tbJ2Score})`);
    }

    // ✅ RÈGLE TENNIS : Calculer le serveur du prochain set AVANT de créer le nouveau set
    // Le serveur du prochain set est l'adversaire du premier serveur du TB
    const nextServer = (this.tieBreakFirstServer === 'J1' ? 'J2' : 'J1');
    console.log(`🎾 Prochain set commencera avec ${nextServer} au service (opposé de ${this.tieBreakFirstServer})`);

    // Vérifier si le match est terminé
    if (this.isMatchFinished()) {
      this.matchFinished = true;
      console.log("🏁 MATCH TERMINÉ (sets gagnants atteints après TB).");
    } else {
      // Préparer le set suivant si configuré
      this.currentSet++;
      const nextFormat = this.matchFormats[this.currentSet] || "NON";
      if (nextFormat === "NON") {
        this.matchFinished = true;
        console.log("🏁 MATCH TERMINÉ (plus de set configuré après TB).");
      } else {
        this.sets.push([0, 0]);
        this.lastTieBreakScores.push(null);
        console.log(`🎯 SET ${this.currentSet + 1} COMMENCÉ`);
      }
    }

    this.tieBreakActive = false;
    this.superTieBreakActive = false;
    this.tieBreakPoints = [0, 0];
    this.tieBreakFirstServer = null;  // 🆕 RESET
    this.jeuPoints = [0, 0];
    
    // ✅ Appliquer le serveur calculé (si match non terminé)
    if (!this.matchFinished) {
      this.server = nextServer;
    }
  }

  getCurrentScore() {
    // SUPER TIE-BREAK S10
    if (this.superTieBreakActive || this.matchFormats[this.currentSet] === "S10") {
      if (this.matchFinished) {
        // 🆕 Calculer le winner
        const { j1, j2 } = this.getSetsWon();
        const winner = j1 > j2 ? 'J1' : (j2 > j1 ? 'J2' : null);
        
        return {
          jeuScore: "0-0",
          sets: this.sets.slice(0, this.currentSet + 1),
          lastTieBreakScores: this.lastTieBreakScores,
          server: this.server,
          tieBreakActive: false,
          superTieBreakActive: false,
          tieBreakFirstServer: this.tieBreakFirstServer,
          matchFinished: this.matchFinished,
          winner: winner,  // 🆕 Ajout du winner
          totalPoints: this.points.length,
        };
      }

      const setsCopy = this.sets.slice(0);
      setsCopy[this.currentSet] = [...this.tieBreakPoints];

      return {
        jeuScore: `${this.tieBreakPoints[0]}-${this.tieBreakPoints[1]}`,
        sets: setsCopy,
        lastTieBreakScores: this.lastTieBreakScores,
        server: this.server,
        tieBreakActive: true,
        superTieBreakActive: true,
        tieBreakFirstServer: this.tieBreakFirstServer,
        matchFinished: this.matchFinished,
        totalPoints: this.points.length,
      };
    }

    // TIE-BREAK normal
    if (this.tieBreakActive) {
      // 🆕 Calculer le winner si le match est terminé
      let winner = null;
      if (this.matchFinished) {
        const { j1, j2 } = this.getSetsWon();
        winner = j1 > j2 ? 'J1' : (j2 > j1 ? 'J2' : null);
      }
      
      return {
        jeuScore: `${this.tieBreakPoints[0]}-${this.tieBreakPoints[1]}`,
        sets: this.sets.slice(0, this.currentSet + 1),
        lastTieBreakScores: this.lastTieBreakScores,
        server: this.server,
        tieBreakActive: true,
        superTieBreakActive: false,
        tieBreakFirstServer: this.tieBreakFirstServer,
        matchFinished: this.matchFinished,
        winner: winner,  // 🆕 Ajout du winner
        totalPoints: this.points.length,
      };
    }

    // JEU NORMAL
    const scores = ["0", "15", "30", "40"];
    let displayJ1, displayJ2;

    if (this.jeuPoints[0] >= 3 && this.jeuPoints[1] >= 3) {
      if (this.jeuPoints[0] > this.jeuPoints[1]) {
        displayJ1 = "AD";
        displayJ2 = "40";
      } else if (this.jeuPoints[1] > this.jeuPoints[0]) {
        displayJ1 = "40";
        displayJ2 = "AD";
      } else {
        displayJ1 = "40";
        displayJ2 = "40";
      }
    } else {
      displayJ1 = scores[Math.min(this.jeuPoints[0], 3)];
      displayJ2 = scores[Math.min(this.jeuPoints[1], 3)];
    }

    // 🆕 Calculer le winner si le match est terminé
    let winner = null;
    if (this.matchFinished) {
      const { j1, j2 } = this.getSetsWon();
      winner = j1 > j2 ? 'J1' : (j2 > j1 ? 'J2' : null);
    }

    return {
      jeuScore: `${displayJ1}-${displayJ2}`,
      sets: this.sets.slice(0, this.currentSet + 1),
      lastTieBreakScores: this.lastTieBreakScores,
      server: this.server,
      tieBreakActive: false,
      superTieBreakActive: false,
      tieBreakFirstServer: this.tieBreakFirstServer,
      matchFinished: this.matchFinished,
      winner: winner,  // 🆕 Ajout du winner
      totalPoints: this.points.length,
    };
  }

  getJeuNumero() {
      if (this.tieBreakActive || this.superTieBreakActive) {
          return 0;
      }
      
      if (this.currentSet >= this.sets.length) return 1;
      const [j1Games, j2Games] = this.sets[this.currentSet];
      return (j1Games + j2Games) + 1;
  }

  undoLastPoint() {
    if (this.points.length === 0) return;
    this.points.pop();
    this.replayAllPoints();
  }

  replayAllPoints() {
    const history = [...this.points];
    this.reset();
    history.forEach((p) => this.addPoint(p.winner, p.pointData));
  }

  setMatchFormats(matchFormats, formatMatch) {
    this.matchFormats = matchFormats;
    if (formatMatch) this.formatMatch = formatMatch;
    this.reset();
  }
}